import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  Upload,
  Table,
  Image,
  message,
  Space,
} from "antd";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { fetchTestTag } from "./FetchTestTags";
import { EditOutlined, SearchOutlined } from "@ant-design/icons";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import Highlighter from "react-highlight-words";
import { token } from "../../constants/Constants";
import SecureImage from "../wellnessExecutive/SecureImage";
import { create_tag, update_tag } from "../../endpoints/API";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const TestTags = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const [tags, setTags] = useState([]);
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState([]);
  const [editingTag, setEditingTag] = useState(null); // State for editing mode
  const isUser = useSelector((state) => state.user.user);
  const [testTags, setTestTags] = useState([]); // State to store test tags data
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  useEffect(() => {
    const getFetchTestTags = async () => {
      try {
        await fetchTestTag(setTestTags, baseUrl, prodTestUrl);
      } catch (error) {
        message.error(error?.response?.data?.error||"Failed to fetch test tags.");
      }
    };

    getFetchTestTags();
  }, []);

  const handleCustomRequest = async ({ file, onSuccess, onError }) => {
    try {
      const base64 = await getBase64(file);
      onSuccess("ok");
    } catch (error) {
      onError(error);
    }
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files.");
    }
    return isImage;
  };

  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const handleAddOrEditTag = async (values) => {
    try {
      const base64Files = await Promise.all(
        fileList
          .filter((file) => file.originFileObj)
          .map((file) => getBase64(file.originFileObj))
      );

      const payload = {
        ...values,
        image: base64Files[0] || editingTag?.image,
        created_by: isUser?.id,
      };

      let response;

      if (editingTag) {
        response = await update_tag(editingTag.id, payload);
      } else {
        response = await create_tag(payload);
      }

      const responseData = response.data;

      if (response.statusText=="OK") {
        message.success(
          editingTag
            ? "Test tag updated successfully!"
            : "Test tag created successfully!"
        );
        setIsModalOpen(false);
        setEditingTag(null); // Reset editing state
        await fetchTestTag(setTestTags, baseUrl, prodTestUrl);
        form.resetFields();
      } else {
        message.error(responseData.error || "Failed to save test tag.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error);
    }
  };

  const handleEdit = (record) => {
    setEditingTag(record);
    setFileList([]);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
    });
    setIsModalOpen(true);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Tag Name",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name"),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (text) => (
        <SecureImage
          // key={`cert-${record.id}`}
          path={text}
          width={100}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button type="link" onClick={() => handleEdit(record)}>
          <EditOutlined />
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-end mb-[1rem]">
        <Button
          type="primary"
          onClick={() => {
            setIsModalOpen(true);
            setEditingTag(null); // Reset editing state for new tag
            form.resetFields();
            setFileList([]);
          }}
        >
          Create Test Tag
        </Button>
      </div>

      <Table
        dataSource={testTags}
        columns={columns}
        bordered
        pagination={{ pageSize: 5 }}
      />

      <Modal
        title={editingTag ? "Edit Test Tag" : "Create Test Tag"}
        visible={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAddOrEditTag}>
          <Form.Item label="Image" name="image">
            <Upload
              customRequest={handleCustomRequest}
              listType="picture-circle"
              fileList={fileList}
              onChange={handleChange}
              beforeUpload={beforeUpload}
              onPreview={handlePreview}
              maxCount={1}
            >
              {fileList.length >= 1 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
            {previewImage && (
              <Image
                wrapperStyle={{ display: "none" }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(""),
                }}
                src={previewImage}
              />
            )}
          </Form.Item>
          <Form.Item
            label="Tag Name"
            name="name"
            rules={[{ required: true, message: "Please enter tag name!" }]}
          >
            <Input placeholder="Enter tag name" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={4} placeholder="Enter description" />
          </Form.Item>

          <div className="flex justify-end">
            <Button
              onClick={() => setIsModalOpen(false)}
              style={{ marginRight: "10px" }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {editingTag ? "Update Tag" : "Add Tag"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default TestTags;
